import { ChevronDown, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type ExportColumn, exportToCsv, exportToXlsx } from "@/lib/export";
import { cn } from "@/lib/utils";

// Standard "Download" affordance that hangs off the right side of any
// data table or panel header. Click → dropdown with CSV / Excel choices.
//
// Two visual tones:
// - `default` — plain outline button, sits inside cards and toolbars on
//   neutral backgrounds.
// - `primary` — light-on-primary styling for use inside the SectionBar's
//   right slot (where the background is the brand color).
//
// Callers pass `filename` without an extension and a `columns` array that
// fully describes the export shape. The button is disabled (with a hint)
// when there are zero rows so users can't download an empty file.

type ExportTone = "default" | "primary";

type ExportButtonProps<T> = {
  filename: string;
  columns: ExportColumn<T>[];
  rows: T[];
  tone?: ExportTone;
  label?: string;
  disabled?: boolean;
  className?: string;
};

export function ExportButton<T>({
  filename,
  columns,
  rows,
  tone = "default",
  label = "Export",
  disabled,
  className,
}: ExportButtonProps<T>) {
  const isDisabled = disabled || rows.length === 0;
  const buttonClass =
    tone === "primary"
      ? "h-7 gap-1 border-primary-foreground/20 bg-primary-foreground/10 px-2 text-primary-foreground text-xs hover:bg-primary-foreground/20 disabled:opacity-50"
      : "h-7 gap-1 border bg-background px-2 text-xs hover:bg-muted disabled:opacity-50";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isDisabled}
          aria-label={`${label} ${rows.length} rows`}
          title={
            isDisabled
              ? "Nothing to export yet"
              : `Export ${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}`
          }
          className={cn(
            "inline-flex shrink-0 items-center rounded-md border font-medium",
            buttonClass,
            className,
          )}
        >
          <Download className="size-3.5" />
          <span className="hidden sm:inline">{label}</span>
          <ChevronDown className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={() => exportToCsv(filename, columns, rows)}>
          <Download className="size-4" />
          <span>CSV</span>
          <span className="ml-auto text-muted-foreground text-xs">.csv</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportToXlsx(filename, columns, rows)}>
          <Download className="size-4" />
          <span>Excel</span>
          <span className="ml-auto text-muted-foreground text-xs">.xlsx</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
